import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import dayjs from "dayjs";
import { myroutes } from "../../../routes/webroute";
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
} from "../../../hooks/useNotifications";
import NotificationDetailModal from "../../../components/NotificationsDetail";
import LayoutActeur from "../inc/layout_acteur";
import { setDataTable } from "../../../utils/helpers";

const MODULES = [
  { value: "", label: "Tous les modules" },
  { value: "dossiers", label: "Dossiers" },
  { value: "audiences", label: "Audiences" },
  { value: "paiements", label: "Paiements" },
  { value: "communications", label: "Communications" },
];

const FOND_NON_LUE = "#f5ecd7"; // beige

export default function NotificationsActeurIndex() {
  const [filtres, setFiltres] = useState({ non_lues: "", module: "" });
  const [selection, setSelection] = useState(null);

  const { data: count = 0 } = useUnreadCount();
  // DataTables pagine côté navigateur : on charge les 100 plus récentes (max autorisé par l'API)
  const { data: resultat, isLoading, isFetching, dataUpdatedAt } = useNotifications(1, {
    ...filtres,
    per_page: 100,
  });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const notifications = resultat?.data ?? [];

  // (Ré)initialise DataTables après chaque rendu de nouvelles données
  useEffect(() => {
    if (!isLoading) setDataTable();
  }, [dataUpdatedAt, isLoading]);

  const changerFiltre = (e) => {
    setFiltres((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const voirDetails = (notif) => {
    if (!notif.lue) markAsRead.mutate(notif.id);
    setSelection(notif);
  };

  return (
    <LayoutActeur>
      <main id="main" className="main">
        <div className="app-content pt-3 p-md-3 p-lg-4 app-card app-card-stat">
          <div className="container-xl app-card-body">
            <div className="row g-3 align-items-center justify-content-between">
              <div className="col-auto">
                <div className="pagetitle">
                  <h1 className="app-page-title mb-0">Notifications</h1>
                  <nav>
                    <ol className="breadcrumb">
                      <li className="breadcrumb-item">
                        <Link to={myroutes.dashboard_acteur}>Accueil</Link>
                      </li>
                      <li className="breadcrumb-item active">Notifications</li>
                    </ol>
                  </nav>
                </div>
              </div>
            </div>

            <div className="row g-3 pb-3 align-items-center justify-content-between">
              <div className="col px-4">
                <h2 className="text-warning">{count}</h2>
                <span>Non lues</span>
              </div>
              <div className="col px-4" style={{ borderLeft: "1px solid #ccc" }}>
                <h2 className="text-primary">{resultat?.total ?? 0}</h2>
                <span>Résultats</span>
              </div>
            </div>

            <div className="row g-3 mb-4 align-items-center justify-content-between">
              <div className="col-auto">
                <h3 className="app-page-title mb-0">Liste des notifications</h3>
              </div>
              <div className="col-auto d-flex flex-wrap gap-2 align-items-center">
                <Form.Select name="non_lues" value={filtres.non_lues} onChange={changerFiltre} size="sm" style={{ width: "auto" }}>
                  <option value="">Toutes</option>
                  <option value="1">Non lues</option>
                </Form.Select>
                <Form.Select name="module" value={filtres.module} onChange={changerFiltre} size="sm" style={{ width: "auto" }}>
                  {MODULES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Form.Select>
                <Button
                  size="sm"
                  variant="warning"
                  className="text-nowrap"
                  disabled={count === 0 || markAllAsRead.isPending}
                  onClick={() => markAllAsRead.mutate()}
                >
                  <i className="bi bi-check2-all me-1"></i>Tout marquer lu
                </Button>
              </div>
            </div>

            <div className="app-card app-card-orders-table shadow-lg mb-5">
              <div className="app-card-body mx-3 py-2">
                {isLoading ? (
                  <p className="text-center text-muted py-4">Chargement…</p>
                ) : (
                  // La key force un tableau neuf à chaque mise à jour, pour que DataTables reparte de zéro
                  <div
                    key={dataUpdatedAt}
                    className="table-responsive mt-4"
                    style={{ opacity: isFetching ? 0.6 : 1 }}
                  >
                    <table className="table table-font table-hover app-table-hover mb-0 data-table">
                      <thead className="table-info mt-2">
                        <tr>
                          <th>N°</th>
                          <th>Date</th>
                          <th>Module</th>
                          <th>Notification</th>
                          <th>Référence</th>
                          <th>Auteur</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="table-group-divider" style={{ textAlign: "left" }}>
                        {notifications.map((notif, index) => (
                          <tr
                            key={notif.id}
                            className={notif.lue ? "table-secondary" : ""}
                            style={notif.lue ? undefined : { "--bs-table-bg": FOND_NON_LUE }}
                          >
                            <td className="cell">{index + 1}</td>
                            <td className="cell text-nowrap">
                              {dayjs(notif.created_at).format("DD/MM/YYYY HH:mm")}
                            </td>
                            <td className="cell">{notif.module_label}</td>
                            <td className="cell">
                              <span className={notif.lue ? "" : "fw-bold"}>{notif.data?.titre}</span>
                              <br />
                              <small>{notif.data?.message}</small>
                            </td>
                            <td className="cell">{notif.data?.reference ?? "—"}</td>
                            <td className="cell">
                              {notif.data?.auteur ? (
                                <>
                                  {notif.data.auteur.nom}
                                  <br />
                                  <small className="text-muted">{notif.data.auteur.profil}</small>
                                </>
                              ) : (
                                <span className="text-muted fst-italic">Système</span>
                              )}
                            </td>
                            <td className="cell">
                              <div className="d-flex gap-2">
                                <Button size="sm" variant="primary" title="Voir les détails" onClick={() => voirDetails(notif)}>
                                  <i className="bi bi-eye"></i>
                                </Button>
                                {!notif.lue && (
                                  <Button
                                    size="sm"
                                    variant="success"
                                    title="Marquer comme lue"
                                    onClick={() => markAsRead.mutate(notif.id)}
                                  >
                                    <i className="bi bi-check2"></i>
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <NotificationDetailModal
        notification={selection}
        onHide={() => setSelection(null)}
        espace="acteur"
      />
    </LayoutActeur>
  );
}